import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as _ from 'lodash';
import type { PayloadAction } from '@reduxjs/toolkit';

import { all, connectToAsyncThunk, map } from '../asyncValue';
import type {
  MatchOverride,
  MatchOverrideWithHashFilter,
  MatchOverrideWithLicenseFilter,
  MatchOverrideWithNewLicense,
  RevisionLicense,
} from '../../types';
import type { AsyncValue } from '../asyncValue';
import type { ReduxStore } from '../store';

import { fetchLicenses, fetchOverrides, fetchProject, fetchRevision } from './api';
import { rebuildRevision } from './rebuildRevision';
import { initialState } from './types';
import type { GLCState, RevisionLicenseAndMatchesAndOverrides } from './types';

// Short-hands for updating AsyncValues.
// TODO: make these part of a class around AsyncValue
const setLoading = <T>(value: AsyncValue<T>): AsyncValue<T> => ({ ...value, isLoading: true, errors: null });
const clearLoading = <T>(value: AsyncValue<T>): AsyncValue<T> => ({ ...value, isLoading: false });
const setError = <T>(value: AsyncValue<T>, error: string): AsyncValue<T> =>
  ({ ...value, errors: [error], isLoading: false });
const clearError = <T>(value: AsyncValue<T>): AsyncValue<T> =>
  ({ ...value, errors: null });
const setValue = <T, U>(value: AsyncValue<T>, newValue: U): AsyncValue<U> =>
  ({ ...value, value: newValue, isLoading: false, errors: null });
const clearValue = <T, U>(value: AsyncValue<T>): AsyncValue<U> =>
  ({ ...value, value: null, isLoading: false, errors: null });

/** An action that sets the error.  Used both as an action and in the extra reducers. */
function projectLookupError(state: GLCState, action: PayloadAction<unknown>): GLCState {
  let message = 'Unknown error';
  if ((action as any).error) { // If there was an error, the async thunk will put it here.
    message = (action as any).error.message;
  } else if (_.isString(action.payload)) { // Dispatched errors show up here
    message = action.payload;
  }
  return {
    ...state,
    project: setError(state.project, message),
    revision: clearLoading(state.revision),
    overrides: clearLoading(state.overrides),
    licenses: clearLoading(state.licenses),
  };
}

/** A Redux slice for the Global License Corrections card. */
export const slice = createSlice({
  name: 'globalLicenseCorrections',
  initialState,

  reducers: {
    projectLookupError, // Allow this action to be dispatched directly, not just from the async thunks.
    clearLookupError: (state) => ({
      ...state,
      project: clearError(state.project),
      revision: clearError(state.revision),
    }),
  },

  // Extra reducers for the actions dispatched by the async thunks.
  extraReducers: builder => {
    builder
      .addCase(fetchProject.pending, (state) => ({
        ...state,
        project: setLoading(state.project),
        revision: setLoading(state.revision),
        matches: setLoading(state.matches),
        scheduledTask: clearValue(state.scheduledTask),
      }))
      .addCase(fetchProject.fulfilled, (state: GLCState, action): GLCState => ({
        ...state,
        project: setValue(state.project, action.payload),
        revision: action.payload.head
          ? setValue(state.revision, action.payload.head)
          : setError(state.revision, 'Project has no head revision'),
      }))
      .addCase(fetchProject.rejected, projectLookupError)
      .addCase(fetchRevision.pending, (state) => ({
        ...state,
        project: setLoading(state.project),
        revision: setLoading(state.revision),
        matches: setLoading(state.matches),
        scheduledTask: clearValue(state.scheduledTask),
      }))
      .addCase(fetchRevision.fulfilled, (state: GLCState, action): GLCState => ({
        ...state,
        project: action.payload.project
          ? setValue(state.project, action.payload.project)
          : setError(state.project, 'Revision has no associated project'),
        revision: setValue(state.revision, action.payload),
      }))
      .addCase(fetchRevision.rejected, projectLookupError);
    connectToAsyncThunk(builder, rebuildRevision, (state, value) => ({ ...state, scheduledTask: value }));
    connectToAsyncThunk(builder, fetchLicenses, (state, value) => ({ ...state, licenses: value }));
    connectToAsyncThunk(builder, fetchOverrides, (state, value) => ({ ...state, overrides: value }));
  },
});

/** The Redux reducer for the Global License Corrections card. */
export const reducer = slice.reducer;

/** The Redux actions for the Global License Corrections card. */
export const actions = slice.actions;

/** The Redux actions for the Global License Corrections card. */
export type Action = ReturnType<typeof slice.actions[keyof typeof slice.actions]>;

/** A selector to extract the Global License Corrections state from global state. */
const stateSelector = (state: ReduxStore): GLCState => state.globalLicenseCorrections;

/** A selector for the project. */
const projectSelector = createSelector([stateSelector], state => state.project);

/** A selector for the revision. */
const revisionSelector = createSelector([stateSelector], state => state.revision);

/** A selector for the matches info. */
const matchOverridesSelector = createSelector([stateSelector], state => state.overrides);

/** A selector for the matches info. */
const licensesSelector = createSelector([stateSelector], state => state.licenses);

/** A selector for the scheduled task. */
const scheduledTaskSelector = createSelector([stateSelector], state => state.scheduledTask);

function isLicenseOverride(
  override: MatchOverride,
): override is MatchOverrideWithLicenseFilter {
  return override.matchFilter.type === 'license';
}

function isHashOverride(
  override: MatchOverride,
): override is MatchOverrideWithHashFilter {
  return override.matchFilter.type === 'hash';
}

function isNewLicenseOverride(
  override: MatchOverride,
): override is MatchOverrideWithNewLicense {
  return override.matchFilter.type === 'new';
}

/** A selector that returns match overrides for licenses grouped by license ID. */
const overridesByLicenseSelector = createSelector([matchOverridesSelector], overridesValue =>
  map(overridesValue, matchOverrides =>
    _.groupBy(
      matchOverrides.filter(isLicenseOverride),
      override => override.matchFilter.licenseId,
    ),
  ),
);

/** A selector that returns match overrides for matches grouped by match hash. */
const overridesByHashSelector: (state: ReduxStore) => AsyncValue<Record<string, MatchOverrideWithHashFilter[]>> =
  createSelector([matchOverridesSelector], (matchOverridesValue) =>
    map(matchOverridesValue, matchOverrides =>
      _.groupBy(
        matchOverrides.filter(isHashOverride),
        override => override.matchFilter.matchIdentityHash,
      ),
    ),
  );

/** Merges two arrays of items by combining elements with the same key. */
function groupAndMergeBy<A, B, Output>(
  aItems: A[],
  aKeyFn: (t: A) => string,
  bItems: B[],
  bKeyFn: (s: B) => string,
  mergeFunction: (key: string, aItems: A[], bItems: B[]) => Output,
): Output[] {
  const groupedTs = _.groupBy(aItems, aKeyFn);
  const groupedSs = _.groupBy(bItems, bKeyFn);
  const keys = _.uniq([...Object.keys(groupedTs), ...Object.keys(groupedSs)]);
  return keys.map(key => mergeFunction(key, groupedTs[key] ?? [], groupedSs[key] ?? []));
}

/** Extracts the licenseId that an override should be associated with. */
function getApplicableLicenseId(override: MatchOverride): string {
  if (isLicenseOverride(override)) {
    return override.matchFilter.licenseId;
  }

  if (isHashOverride(override)) {
    // Use the new license ID if it has one, because this match was moved.
    // Otherwise it's a removal and the old ID should be stored in the filter.
    // If neither has a value then it's unknown.
    return override.newLicenseIdOrRemove ?? override.matchFilter.licenseId ?? 'unknown';
  }

  if (isNewLicenseOverride(override)) {
    return override.newLicenseIdOrRemove ?? 'unknown';
  }

  return 'unknown';
}

/**
 * Combines the licenses and overrides for a specific licenseId.
 * The output is a structure containing all the licenses for that ID, all the
 * overrides that apply to that license, and all the matches for those licenses
 * merged with their overrides.
 */
function combineLicenseAndOverrides(
  licenseId: string,
  licenses: RevisionLicense[],
  allOverrides: MatchOverride[],
): RevisionLicenseAndMatchesAndOverrides {
  const newOverrides = allOverrides.filter(isNewLicenseOverride);
  const licenseOverrides = allOverrides.filter(isLicenseOverride);
  const hashOverrides = allOverrides.filter(isHashOverride);

  return {
    licenseId,
    licenses,
    licenseOverrides,
    newOverrides,
    matches: groupAndMergeBy(
      licenses[0]?.matches || [],
      match => match.matchIdentityHash,
      hashOverrides,
      override => override.matchFilter.matchIdentityHash,
      (_hash, matches, overrides) => {
        return {
          match: matches[0],
          overrides,
        };
      },
    ),
  };
}

/** A selector that returns the matches and overrides merged together into a single list. */
const licensesAndOverridesSelector: (state: any) => AsyncValue<RevisionLicenseAndMatchesAndOverrides[]> =
  createSelector(
    [licensesSelector, matchOverridesSelector],
    (licensesValue, overridesByLicenseValue) =>
      map(
        all([licensesValue, overridesByLicenseValue] as const),
        ([licenses, overrides]) =>
          Object.values(
            groupAndMergeBy(
              licenses,
              license => license.licenseId ?? 'unknown',
              overrides,
              getApplicableLicenseId,
              combineLicenseAndOverrides,
            ),
          ),
      ),
  );

export const selectors = {
  stateSelector,
  projectSelector,
  revisionSelector,
  licensesSelector,
  matchOverridesSelector,
  scheduledTaskSelector,
  licensesAndOverridesSelector,
  overridesByLicenseSelector,
  overridesByHashSelector,
};
